import { useCallback } from 'react';

import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { DonorSchedule } from '@/rtk-query.gql';

import { TRAVEL_LOCATION_NAME, validationFields } from '../constants';
import { MissingFieldsResult } from '../types';
import { isNoStimSchedule } from '../util/fshDefaulting';

const isNotRequiredForSchedule = (
  field: keyof DonorSchedule,
  donorSchedule: DonorSchedule,
) => {
  switch (field) {
    case 'fshId':
      return isNoStimSchedule(donorSchedule.scheduleMaster?.name);
    case 'colCode':
      return (
        donorSchedule.semenLocation?.name
          ?.toUpperCase()
          .includes(TRAVEL_LOCATION_NAME) ?? false
      );
    default:
      return false;
  }
};

export const useMissingFields = (donorSchedules: DonorSchedule[] | null) => {
  const isFreezeFieldsNotEditable = useFeatureFlag(
    'Freeze_Fields_Not_Editable',
  );
  const isMissingFieldsExceptionsEnabled = useFeatureFlag(
    'ds_missing_fields_exceptions',
  );

  const getMissingFields = useCallback(() => {
    if (!donorSchedules) {
      return validationFields;
    }

    const results: MissingFieldsResult[] = [];

    donorSchedules.forEach((donorSchedule) => {
      if (!donorSchedule) return;

      const missingFields = validationFields
        .filter((field) => {
          const isFreezeRelated =
            field.field === 'freeze' || field.field === 'freezeMethodId';

          const conditionMet =
            isFreezeRelated && !isFreezeFieldsNotEditable
              ? true
              : field.condition
                ? field.condition(donorSchedule)
                : true;

          if (!conditionMet) return false;

          if (
            isMissingFieldsExceptionsEnabled &&
            isNotRequiredForSchedule(field.field, donorSchedule)
          ) {
            return false;
          }

          const value = donorSchedule[field.field];
          return (
            field.required &&
            (value === null || value === undefined || value === '')
          );
        })
        .map((field) => field.label);

      if (missingFields.length > 0) {
        results.push({
          donorId: donorSchedule.id || '',
          donorName:
            donorSchedule.donor?.name ||
            donorSchedule.donorId ||
            'Unknown Donor',
          missingFields,
        });
      }
    });

    return results;
  }, [
    donorSchedules,
    isFreezeFieldsNotEditable,
    isMissingFieldsExceptionsEnabled,
  ]);

  return {
    getMissingFields,
  };
};
